import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { gray } from "../../../theme/themePrimitives";
import { PdfPageNavigation } from "./PdfPageNavigation";
import { PdfViewer } from "./PdfViewer";

interface PdfPageByPageViewerProps {
  assignmentTitle: string;
  url: string;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  onDocumentLoad?: (numPages: number) => void;
  // Fires once the last page has been reached and rendered.
  onReachedEnd?: () => void;
  requiresAcknowledgement?: boolean;
}

export const PdfPageByPageViewer: React.FC<PdfPageByPageViewerProps> = ({
  url,
  initialPage = 1,
  onPageChange,
  onDocumentLoad,
  onReachedEnd,
  assignmentTitle,
  requiresAcknowledgement,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  // Tracks whether the current page has finished rendering, i.e. has been
  // "visited" — Next stays disabled until then
  const [pageRendered, setPageRendered] = useState(false);

  useEffect(() => {
    if (numPages && currentPage === numPages && pageRendered) {
      onReachedEnd?.();
    }
  }, [numPages, currentPage, pageRendered, onReachedEnd]);

  const handleNext = useCallback(() => {
    if (!numPages || currentPage >= numPages || !pageRendered) return;
    const next = currentPage + 1;
    setCurrentPage(next);
    setPageRendered(false);
    onPageChange?.(next);
  }, [currentPage, numPages, pageRendered, onPageChange]);

  const handlePrev = useCallback(() => {
    if (currentPage <= 1) return;
    const prev = currentPage - 1;
    setCurrentPage(prev);
    setPageRendered(false);
    onPageChange?.(prev);
  }, [currentPage, onPageChange]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        border: `1px solid ${gray[200]}`,
        backgroundColor: "white",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <PdfViewer
            url={url}
            pageNumber={currentPage}
            onDocumentLoad={(loadedNumPages) => {
              setNumPages(loadedNumPages);
              onDocumentLoad?.(loadedNumPages);
            }}
            onPageRenderSuccess={() => setPageRendered(true)}
          />
        </Box>
        {numPages != null && (
          <PdfPageNavigation
            currentPage={currentPage}
            numPages={numPages}
            canGoNext={pageRendered}
            onPrev={handlePrev}
            onNext={handleNext}
            requiresAcknowledgement={requiresAcknowledgement}
          />
        )}
      </Box>
    </Box>
  );
};
